import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, X, Crown, Send, Podcast } from "lucide-react";
import api, { timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useArticlePath } from "@/lib/i18n";
import { Logo } from "@/components/Logo";
import { MORE_GROUPS, SECTORS, track } from "./navConfig";
import { SectorArt } from "./SectorArt";
import { CoinIcon, Pct, fmtPrice, FOCUS } from "./primitives";

type Props = { open: boolean; onClose: () => void };

const Row = ({ to, label, onClose, sub }: { to: string; label: string; onClose: () => void; sub?: boolean }) => (
  <Link to={to} onClick={() => { track("nav_click", { to, mobile: true }); onClose(); }}
    className={`block py-2.5 ${sub ? "text-sm text-gray-500" : "text-[15px] font-medium text-gray-300"} hover:text-white active:text-white transition-colors ${FOCUS}`}>
    {label}
  </Link>
);

export const MobileNav = ({ open, onClose }: Props) => {
  const [expanded, setExpanded] = useState<string | null>(null);
  const { user } = useAuth();
  const location = useLocation();
  const toArticle = useArticlePath();

  const { data: markets } = useQuery({
    queryKey: ["markets"],
    queryFn: async () => (await api.get("/markets")).data,
    staleTime: 90 * 1000,
    enabled: open,
  });
  const coins = (markets?.coins || []).filter((c: any) => ["BTC", "ETH", "SOL"].includes(c.symbol));
  const btcEth = coins.filter((c: any) => c.symbol !== "SOL");

  const { data: trending } = useQuery({
    queryKey: ["nav-trending"],
    queryFn: async () => (await api.get("/trending/most-read")).data,
    staleTime: 2 * 60 * 1000,
    enabled: open,
  });
  const topStories = (trending?.items || []).slice(0, 2);

  const { data: activity } = useQuery({
    queryKey: ["nav-sectors-activity"],
    queryFn: async () => (await api.get("/sectors/activity")).data,
    staleTime: 5 * 60 * 1000,
    enabled: open,
  });
  const topSector = activity?.top ? SECTORS.find((s) => s.slug === activity.top.slug) : null;

  useEffect(() => { onClose(); }, [location.pathname, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const Section = ({ id, title, children }: { id: string; title: string; children: React.ReactNode }) => {
    const isOpen = expanded === id;
    return (
      <div className="border-b border-white/[0.07]">
        <button type="button" data-testid={`mobile-expand-${id}`}
          aria-expanded={isOpen} aria-controls={`mobile-subs-${id}`}
          onClick={() => setExpanded(isOpen ? null : id)}
          className={`w-full flex items-center justify-between py-3.5 text-left text-base font-semibold ${isOpen ? "text-[#00E5FF]" : "text-gray-200"} ${FOCUS}`}>
          {title}
          <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
        </button>
        {isOpen && (
          <div id={`mobile-subs-${id}`} data-testid={`mobile-subs-${id}`}
            className="pb-4 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-1 motion-safe:duration-200">
            {children}
          </div>
        )}
      </div>
    );
  };

  return createPortal(
    <div className="fixed inset-0 z-[70] bg-[#000000] flex flex-col lg:hidden" data-testid="nav-mobile-menu"
      role="dialog" aria-modal="true" aria-label="Navigation menu">
      <div className="flex items-center justify-between px-4 h-14 border-b border-white/10 shrink-0">
        <Logo compact />
        <button type="button" data-testid="mobile-close" aria-label="Close menu" onClick={onClose}
          className={`p-2 -mr-2 text-gray-400 hover:text-white transition-colors rounded ${FOCUS}`}>
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 pb-[calc(env(safe-area-inset-bottom)+88px)]">
        <Section id="latest" title="Latest">
          <Row to="/latest" label="Breaking" onClose={onClose} sub />
          <Row to="/research" label="Deep Dives" onClose={onClose} sub />
          <Row to="/latest" label="Most Read" onClose={onClose} sub />
          <Row to="/" label="Editor's Picks" onClose={onClose} sub />
          {topStories.length > 0 && (
            <div className="mt-1 mb-2 rounded-lg border border-white/[0.08] bg-white/[0.02] divide-y divide-white/[0.06]">
              {topStories.map((a: any) => (
                <Link key={a.slug} to={toArticle(a.slug)} onClick={onClose} data-testid="mobile-trending-story"
                  className={`block px-3 py-2.5 ${FOCUS}`}>
                  <span className="font-mono text-[8px] font-bold uppercase tracking-widest text-[#00E5FF]">Trending</span>
                  <span className="block text-sm font-semibold text-gray-200 leading-snug line-clamp-2 mt-0.5">{a.title}</span>
                  <span className="block font-mono text-[9px] uppercase tracking-wider text-gray-600 mt-1">{timeAgo(a.published_at)}</span>
                </Link>
              ))}
            </div>
          )}
          {btcEth.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 mb-2"
              data-testid="mobile-live-layer">
              {btcEth.map((c: any) => (
                <span key={c.symbol} className="flex items-center gap-1.5">
                  <CoinIcon symbol={c.symbol} size={18} />
                  <span className="font-mono text-xs text-white">{fmtPrice(c.price)}</span>
                  <Pct v={c.change_24h} />
                </span>
              ))}
            </div>
          )}
          <Link to="/latest" onClick={onClose} className="inline-block text-sm font-bold text-[#00E5FF] py-1.5">
            All Latest →
          </Link>
        </Section>

        <div className="border-b border-white/[0.07]">
          <Link to="/briefs" data-testid="mobile-link-briefs" onClick={onClose}
            className={`block py-3.5 text-base font-semibold text-gray-200 hover:text-white ${FOCUS}`}>
            Daily Brief
          </Link>
        </div>

        <Section id="sectors" title="Sectors">
          {topSector && (
            <Link to={topSector.to} onClick={onClose} data-testid="mobile-trending-sector"
              className="flex items-center gap-2 rounded-lg border border-[#00E5FF]/25 bg-[#00E5FF]/[0.04] px-3 py-2 mb-2">
              <span className="font-mono text-[8px] font-bold uppercase tracking-widest text-[#00E5FF]">Trending</span>
              <span className="text-sm font-bold text-white">{topSector.label}</span>
              <span className="font-mono text-[10px] text-gray-500 ml-auto">
                {activity.top.count} stories · {activity.top_share_pct}% of 24h
              </span>
            </Link>
          )}
          <div className="grid grid-cols-2 gap-2">
            {SECTORS.map((s) => (
              <Link key={s.slug} to={s.to} onClick={onClose} data-testid={`mobile-sector-${s.slug}`}
                className={`relative overflow-hidden flex items-center rounded-lg border border-white/10 bg-[#0d0e14] px-3 py-2.5 text-sm font-semibold text-gray-200 active:bg-[#12131d] ${FOCUS}`}>
                <SectorArt kind={s.slug} className="absolute inset-y-0 right-0 h-full w-1/2 opacity-50" />
                <span className="relative">{s.label}</span>
              </Link>
            ))}
          </div>
        </Section>

        <Section id="markets" title="Markets">
          <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] divide-y divide-white/[0.06] mb-2">
            {coins.map((c: any) => (
              <Link key={c.symbol} to={`/coin/${c.symbol}`} onClick={onClose}
                className="flex items-center gap-2.5 px-3 py-2">
                <CoinIcon symbol={c.symbol} size={22} />
                <span className="font-mono text-xs font-bold text-gray-300">{c.symbol}</span>
                <span className="font-mono text-sm text-white ml-auto">{fmtPrice(c.price)}</span>
                <Pct v={c.change_24h} className="w-14 text-right" />
              </Link>
            ))}
            {coins.length === 0 && (
              <p className="font-mono text-[11px] text-gray-600 px-3 py-2.5">Market data temporarily unavailable</p>
            )}
          </div>
          <Row to="/coin/BTC" label="Live Charts" onClose={onClose} sub />
          <Row to="/entities" label="Entities" onClose={onClose} sub />
          <Row to="/account" label="Watchlist" onClose={onClose} sub />
          <Row to="/regulation-tracker" label="Regulation Tracker" onClose={onClose} sub />
          <Link to="/markets" onClick={onClose} className="inline-block text-sm font-bold text-[#00E5FF] py-1.5">
            View Markets →
          </Link>
        </Section>

        <Section id="premium" title="Premium">
          <p className="text-sm text-gray-400 mb-1">Exclusive analysis, Regulation Radar and ad-free reading.</p>
          <Row to="/radar" label="Regulation Radar" onClose={onClose} sub />
          <Row to="/research" label="Premium Research" onClose={onClose} sub />
          {user?.is_premium ? (
            <Link to="/radar" onClick={onClose} data-testid="mobile-premium-cta"
              className="mt-2 flex items-center justify-center gap-2 rounded-md bg-[#7C3AED]/20 border border-[#7C3AED]/50 text-white font-bold text-sm py-2.5">
              <Crown className="h-4 w-4 text-[#F59E0B]" /> Open Premium →
            </Link>
          ) : (
            <Link to="/premium" onClick={onClose} data-testid="mobile-premium-cta"
              className="sparkle-btn mt-2 flex items-center justify-center rounded-md bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white font-bold text-sm py-2.5">
              Become a Member →
            </Link>
          )}
        </Section>

        <Section id="more" title="More">
          {MORE_GROUPS.filter((g) => g.label !== "Connect").map((g) => (
            <div key={g.label} className="mb-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A78BFA] mb-1">{g.label}</p>
              {g.links.map((l) =>
                l.external ? (
                  <a key={l.label} href={l.to} target={l.to.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
                    className="block py-1.5 text-sm text-gray-500 hover:text-white">
                    {l.label}
                  </a>
                ) : (
                  <Row key={l.label} to={l.to} label={l.label} onClose={onClose} sub />
                ))}
            </div>
          ))}
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#00E5FF] mb-2">Connect</p>
          <div className="flex flex-wrap gap-2 mb-3" data-testid="mobile-connect-rail">
            <a href="https://x.com/newslayerapp" target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.02] px-3.5 py-1.5 text-xs font-semibold text-gray-400 active:text-white">
              <svg viewBox="0 0 24 24" fill="currentColor" className="h-3.5 w-3.5" aria-hidden>
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
              X
            </a>
            <a href="https://t.me/newslayerapp" target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.02] px-3.5 py-1.5 text-xs font-semibold text-gray-400 active:text-white">
              <Send className="h-3.5 w-3.5" /> Telegram
            </a>
            <Link to="/briefs" onClick={onClose}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.02] px-3.5 py-1.5 text-xs font-semibold text-gray-400 active:text-white">
              <Podcast className="h-3.5 w-3.5" /> Podcast
            </Link>
          </div>
          <Link to="/pr" onClick={onClose} data-testid="mobile-pr-cta"
            className="flex items-center justify-center gap-2 rounded-md bg-[#7C3AED] text-white font-bold text-sm py-2.5">
            <Send className="h-3.5 w-3.5" /> Submit a Release →
          </Link>
        </Section>
      </div>
    </div>,
    document.body,
  );
};
